import api from './client';

export interface DomiciliationRecord {
  id: number;
  employee: number;
  employee_name: string;
  employee_matricule: string;
  bank_name: string;
  account_number: string;
  salary: string;
  establishment_date: string | null;
  created_by: number | null;
  created_by_name: string;
  created_at: string;
}

export interface NewDomiciliation {
  employee: number;
  bank_name: string;
  account_number: string;
  salary: string;
  establishment_date: string;
}

const LEGACY_KEY = 'domiciliations';
const LEGACY_SYNCED_FLAG = 'domiciliations_synced_at';

export async function fetchDomiciliations(): Promise<DomiciliationRecord[]> {
  const { data } = await api.get('documents/domiciliations/');
  // L'API peut être paginée ou non selon la configuration DRF.
  return Array.isArray(data) ? data : (data?.results ?? []);
}

export async function createDomiciliation(payload: NewDomiciliation): Promise<DomiciliationRecord> {
  const { data } = await api.post('documents/domiciliations/', payload);
  return data;
}

/**
 * Reprise des domiciliations historiques.
 *
 * Avant l'existence de l'API, chaque attestation n'était enregistrée que dans
 * le localStorage du navigateur qui l'avait générée. On pousse ces
 * enregistrements vers le serveur pour qu'ils deviennent visibles par tous.
 *
 * L'opération est idempotente côté serveur (clé `legacy_key` unique) : la
 * rejouer ne crée jamais de doublon. Le localStorage n'est jamais vidé, il
 * reste un filet de sécurité tant que la reprise n'est pas confirmée.
 */
export async function syncLegacyDomiciliations(): Promise<number> {
  let stored: unknown;
  try {
    stored = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
  } catch {
    return 0; // localStorage corrompu : rien à reprendre, on n'échoue pas pour autant
  }
  if (!Array.isArray(stored) || stored.length === 0) return 0;

  const records = stored
    .filter((r: any) => r && r.employee_id)
    .map((r: any) => ({
      legacy_key: String(r.id ?? `${r.employee_id}-${r.created_at}`),
      employee: Number(r.employee_id),
      bank_name: r.bank_name || '',
      account_number: r.account_number || '',
      salary: r.salary || '',
      establishment_date: r.establishment_date || null,
      created_at: r.created_at || null,
    }));

  if (records.length === 0) return 0;

  const { data } = await api.post('documents/domiciliations/sync-legacy/', { records });
  localStorage.setItem(LEGACY_SYNCED_FLAG, new Date().toISOString());
  return data?.created ?? 0;
}
